import { 
    Checkbox, 
    FormControl, 
    FormControlLabel, 
    InputLabel, 
    MenuItem, 
    Select, 
    TextField, 
    Typography,
    Box,
    Paper,
    Grid,
    Divider,
    InputAdornment,
    Chip,
    Avatar
} from '@mui/material'
import React from 'react'
import { 
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Public as PublicIcon,
    Diversity3 as DiversityIcon,
    Wc as GenderIcon,
    Category as CategoryIcon,
    Description as DescriptionIcon,
    Payment as PaymentIcon,
    Image as ImageIcon
} from '@mui/icons-material'
import { ethnicity, genders, niches, region } from '../../Utils/constants'
import MuiPhoneInput from '../MuiPhoneInput'

export default function CreatorInfo({
    creatorName,
    setCreatorName,
    creatorRegion,
    setCreatorRegion,
    race,
    setRace,
    gender,
    setGender,
    location,
    setLocation,
    primaryMarket,
    setPrimaryMarket,
    notesContentStyle,
    setNotesContentStyle,
    contactEmail,
    setContactEmail,
    phoneNumber,
    setPhoneNumber,
    validationError,
    paymentEmail,
    setPaymentEmail,
    emailSame,
    setEmailSame,
    pfphref,
    setPfphref,
    require = true
}) {

    const handleEmailSameChange = (event) => {
        setEmailSame(event.target.checked);
        if (event.target.checked) {
            setPaymentEmail(contactEmail);
        } else {
            setPaymentEmail("");
        }
    };

    return (
        <Box sx={{ maxWidth: 900, margin: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Creator Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Tell us about yourself and help brands find you
                </Typography>
            </Box>

            {/* Profile Photo Preview */}
            {pfphref !== undefined && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Avatar 
                            src={pfphref} 
                            sx={{ width: 80, height: 80, border: '3px solid #e0e0e0' }}
                        >
                            <ImageIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                margin="dense"
                                label="Profile Photo URL"
                                type="text"
                                fullWidth
                                value={pfphref}
                                onChange={(e) => setPfphref(e.target.value)}
                                variant="outlined"
                                placeholder="https://example.com/your-photo.jpg"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ImageIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Basic Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <TextField
                    margin="dense"
                    label="Display Name"
                    type="text"
                    fullWidth
                    value={creatorName}
                    onChange={(e) => setCreatorName(e.target.value)}
                    variant="outlined"
                    required
                    error={!!validationError}
                    helperText={validationError || "This is how brands will see you"}
                    disabled={!setCreatorName}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth margin="dense">
                            <InputLabel>Race (Optional)</InputLabel>
                            <Select
                                value={race}
                                required={require}
                                onChange={(e) => setRace(e.target.value)}
                                label="Race (Optional)"
                                startAdornment={
                                    <InputAdornment position="start">
                                        <DiversityIcon color="action" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="">
                                    <em>Prefer not to say</em>
                                </MenuItem>
                                {ethnicity.map((value) => (
                                    <MenuItem key={value} value={value}>{value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth margin="dense" required>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={gender}
                                required={require}
                                onChange={(e) => setGender(e.target.value)}
                                label="Gender"
                                startAdornment={
                                    <InputAdornment position="start">
                                        <GenderIcon color="action" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="">
                                    <em>Select gender</em>
                                </MenuItem>
                                {genders.map((value) => (
                                    <MenuItem key={value} value={value}>{value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Location & Market */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon color="primary" />
                    Location & Market
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth margin="dense" required>
                            <InputLabel>Creator Region</InputLabel>
                            <Select
                                label="Creator Region"
                                value={creatorRegion}
                                required={require}
                                onChange={(e) => setCreatorRegion(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <PublicIcon color="action" />
                                    </InputAdornment>
                                }
                            >
                                {region.map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="location"
                            margin="dense"
                            label="Location"
                            type="text"
                            fullWidth
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            variant="outlined"
                            required={require}
                            placeholder="City, State/Country"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LocationIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                <FormControl variant="outlined" fullWidth margin="dense" required sx={{ mt: 1 }}>
                    <InputLabel>Primary Markets</InputLabel>
                    <Select
                        name="primary-market"
                        value={primaryMarket}
                        onChange={(e) => setPrimaryMarket(e.target.value)}
                        multiple
                        label="Primary Markets"
                        startAdornment={
                            <InputAdornment position="start">
                                <CategoryIcon color="action" />
                            </InputAdornment>
                        }
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" color="primary" variant="outlined" />
                                ))}
                            </Box>
                        )}
                    >
                        {niches.map((market) => (
                            <MenuItem key={market} value={market}>
                                {market}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    name="bio"
                    margin="dense"
                    label="Bio"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Tell us about yourself, your content style, and what makes you unique..."
                    variant="outlined"
                    value={notesContentStyle}
                    required={require}
                    onChange={(e) => setNotesContentStyle(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                <DescriptionIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mt: 2 }}
                />
            </Paper>

            {/* Contact Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" />
                    Contact Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                    name="contact-email"
                    margin="dense"
                    label="Contact Email"
                    type="email"
                    fullWidth
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    variant="outlined"
                    required={require}
                    helperText="We'll use this to reach you about opportunities"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                    <MuiPhoneInput
                        country={'us'}
                        preferredCountries={['us']}
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        isValid={(value) => value.length >= 10}
                        required
                    />
                </Box>

                {paymentEmail !== undefined && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentIcon color="action" fontSize="small" />
                            Payment Details
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={emailSame} 
                                    onChange={handleEmailSameChange}
                                    color="primary"
                                />
                            }
                            label="Payment and contact email are the same"
                            sx={{ mb: 2 }}
                        />
                        
                        <TextField
                            margin="dense"
                            label="PayPal Payment Email"
                            type="email"
                            fullWidth
                            disabled={emailSame}
                            value={paymentEmail}
                            onChange={(e) => setPaymentEmail(e.target.value)}
                            variant="outlined"
                            required={!emailSame}
                            placeholder={emailSame ? contactEmail : "paypal@example.com"}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PaymentIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    )
}